import React, { useState, useEffect } from 'react';
import { LogOut, CheckCircle, ArrowRight, Shield } from 'lucide-react';
import { ReactComponent as TraceLogo } from '../assets/trace-logo.svg';

interface LogoutProps {
  userName: string;
  onBackToLogin: () => void;
}

export const Logout: React.FC<LogoutProps> = ({ userName, onBackToLogin }) => {
  const [phase, setPhase] = useState<'signing-out' | 'done'>('signing-out');

  useEffect(() => {
    // Simulate sign-out process
    const timer = setTimeout(() => {
      setPhase('done');
    }, 1800);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-surface-50 flex items-center justify-center p-6 relative overflow-hidden">
      {/* Subtle background mesh */}
      <div className="absolute inset-0 bg-mesh-gradient opacity-60" />

      {/* Decorative elements */}
      <div className="absolute top-[20%] left-[15%] w-[300px] h-[300px] rounded-full bg-brand-400/3 blur-[100px]" />
      <div className="absolute bottom-[20%] right-[15%] w-[250px] h-[250px] rounded-full bg-brand-300/4 blur-[80px]" />

      <div className="relative z-10 w-full max-w-md">
        {/* Card */}
        <div className="glass-card p-10 text-center animate-fade-in">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <TraceLogo className="h-8 w-auto opacity-40" />
          </div>

          {phase === 'signing-out' ? (
            <>
              {/* Signing out animation */}
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 rounded-2xl bg-surface-100 flex items-center justify-center relative">
                  <LogOut size={32} className="text-surface-400" />
                  {/* Spinning ring */}
                  <div className="absolute inset-0 rounded-2xl border-2 border-transparent border-t-brand-400 animate-spin" style={{ animationDuration: '1.2s' }} />
                </div>
              </div>

              <h2 className="text-xl font-bold text-surface-900 mb-2">
                Signing you out...
              </h2>
              <p className="text-sm text-surface-500">
                Securely ending your session
              </p>

              {/* Progress dots */}
              <div className="flex items-center justify-center gap-1.5 mt-6 dot-pulse">
                <span className="w-2 h-2 rounded-full bg-brand-400" />
                <span className="w-2 h-2 rounded-full bg-brand-400" />
                <span className="w-2 h-2 rounded-full bg-brand-400" />
              </div>
            </>
          ) : (
            <>
              {/* Success state */}
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 rounded-2xl bg-emerald-50 flex items-center justify-center animate-scale-in">
                  <CheckCircle size={36} className="text-emerald-500" />
                </div>
              </div>

              <h2 className="text-xl font-bold text-surface-900 mb-2 animate-slide-up">
                You've been signed out
              </h2>
              <p className="text-sm text-surface-500 mb-1 animate-slide-up" style={{ animationDelay: '0.05s' }}>
                Goodbye, {userName}
              </p>
              <p className="text-xs text-surface-400 animate-slide-up" style={{ animationDelay: '0.1s' }}>
                Your session has been securely terminated
              </p>

              {/* Security confirmation */}
              <div className="flex items-center justify-center gap-2 mt-5 mb-8 animate-slide-up" style={{ animationDelay: '0.15s' }}>
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-100">
                  <Shield size={12} className="text-emerald-600" />
                  <span className="text-[11px] font-semibold text-emerald-700">Session cleared securely</span>
                </div>
              </div>

              {/* Back to login button */}
              <button
                onClick={onBackToLogin}
                className="w-full flex items-center justify-center gap-2.5 py-3 px-5 rounded-xl text-sm font-semibold
                  bg-brand-400 text-white hover:bg-brand-500 active:bg-brand-600
                  transition-all duration-200 shadow-sm hover:shadow-md group
                  animate-slide-up"
                style={{ animationDelay: '0.2s' }}
              >
                <span>Back to Sign In</span>
                <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5" />
              </button>

              {/* Session details */}
              <div className="mt-6 pt-5 border-t border-surface-100 animate-slide-up" style={{ animationDelay: '0.25s' }}>
                <div className="flex items-center justify-between text-[11px] text-surface-400">
                  <span>Session ended</span>
                  <span className="font-medium">{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                <div className="flex items-center justify-between text-[11px] text-surface-400 mt-1.5">
                  <span>Date</span>
                  <span className="font-medium">{new Date().toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-[11px] text-surface-300 mt-6 font-medium">
          NimQ TRACE &middot; Secure Compliance Platform
        </p>
      </div>
    </div>
  );
};
